import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal, Pagination } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import moment from 'moment';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import {
  DbFile,
  DbFileActionLog,
  DbFolder,
  DbFolderActionLog,
  DbModuleFile,
} from '~src/types/db';
import {
  ItemLogReturnType,
  LOG_ITEM_TYPE,
  useItemActionLog,
} from '~src/hooks/api-hooks/useItemActionLog';
import icon from '~components/_common/icon/icon';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { LOG_ACTION } from './LogModal/LogModal';
import { formatDate } from '~src/utils/formatDate';

interface ItemLogModalProps extends Partial<ModalProps> {
  folder?: DbFolder;
  file?: DbFile;
  moduleFile?: DbModuleFile;
}

const TAKE_LIMIT = 12;

export const ItemLogModal: React.FC<ItemLogModalProps> = ({
  folder,
  file,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [before, setBefore] = useState(
    window.sessionStorage.getItem('timeMachineDate')
      ? moment(window.sessionStorage.getItem('timeMachineDate')).unix()
      : moment().unix()
  );
  const [skipLimit, setSkipLimit] = useState(0);
  const [totalNumberOfItems, setTotalNumberOfItems] = useState<number>();

  const itemActionLogQuery = useItemActionLog(
    {
      itemType: file ? LOG_ITEM_TYPE.FILE : LOG_ITEM_TYPE.FOLDER,
      itemId: file ? file.id : folder?.id || 0,
      take: TAKE_LIMIT,
      skip: skipLimit * TAKE_LIMIT,
      before,
      general: true,
    },
    { enabled: modalProps.visible }
  );

  useEffect(() => {
    modalProps.visible && itemActionLogQuery.refetch();
  }, [modalProps.visible, file?.id, folder?.id]);

  useEffect(() => {
    if (!totalNumberOfItems) {
      setTotalNumberOfItems(itemActionLogQuery.data?.totalCount);
    }
  }, [itemActionLogQuery.data]);

  const itemActionLogColumns: TableProps<
    ItemLogReturnType['logs']
  >['columns'] = [
    {
      title: intl.formatMessage({ id: 'date-and-time' }),
      dataIndex: 'action_at',
      key: 'date',
      render: (action_at) => formatDate(action_at, 'long'),
      width: '20%',
    },
    {
      title: intl.formatMessage({ id: 'name' }),
      dataIndex: file ? 'registration_name' : 'folder_name',
      key: 'name',
      width: '25%',
    },
    {
      title: intl.formatMessage({ id: 'path' }),
      dataIndex: file ? 'registration_path' : 'folder_path',
      key: 'path',
      width: '30%',
    },
    {
      title: intl.formatMessage({ id: 'action' }),
      dataIndex: 'action_type',
      key: 'action',
      render: (action) =>
        Object.keys(LOG_ACTION).filter((x) => x === action).length >= 1
          ? intl.formatMessage({ id: LOG_ACTION[action] })
          : action,
      width: '10%',
    },
    {
      title: intl.formatMessage({ id: 'by' }),
      dataIndex: 'create_by_name',
      key: 'by',
      width: '15%',
    },
  ];

  const itemFileLogSource = useMemo(
    () =>
      ((itemActionLogQuery.data?.logs as DbFileActionLog[]) || []).map(
        (element) => ({
          ...element,
          key: element.action_id,
        })
      ),
    [itemActionLogQuery.data, modalProps.visible]
  );

  const itemFolderLogSource = useMemo(
    () =>
      ((itemActionLogQuery.data?.logs as DbFolderActionLog[]) || []).map(
        (element) => ({
          ...element,
          key: element.action_id,
        })
      ),
    [itemActionLogQuery.data, modalProps.visible]
  );

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage(
            { id: 'individual-log' },
            {
              fileType: folder
                ? intl.formatMessage({ id: 'folder' })
                : intl.formatMessage({ id: 'file-item' }),
            }
          )}
          icon={['far', 'file-alt']}
        />
      }
      bodyStyle={{ height: 'min(65vh, 600px)' }}
      centered
      width="min(95vw, 1150px)"
      closeIcon={icon('far', 'times', styles.closeBtn, 'close')}
      footer={<Fragment />}
      {...modalProps}>
      <div style={{ position: 'relative', height: 'min(65vh, 600px)' }}>
        <Table<any>
          scroll={{ y: 'min(50vh, 450px)', scrollToFirstRowOnChange: true }}
          showSorterTooltip={false}
          columns={itemActionLogColumns}
          dataSource={file ? itemFileLogSource : itemFolderLogSource}
          pagination={false}
          loading={itemActionLogQuery.isLoading}
        />
        {totalNumberOfItems && totalNumberOfItems > 12 && (
          <Pagination
            style={{ position: 'absolute', bottom: 40, right: 0 }}
            total={totalNumberOfItems}
            pageSize={12}
            showSizeChanger={false}
            onChange={(page) => {
              setSkipLimit(page - 1);
            }}
          />
        )}
      </div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  closeBtn: {
    color: theme.colors.darkGray,
    marginTop: '18px',
    fontSize: '20px',
    transition: '0.3s',
  },
});
